/* eslint-disable */
/**
 * Deep merges two objects, with the second object taking precedence over the first.
 * Arrays are replaced entirely (not merged element by element).
 *
 * @param target - The target object to merge into
 * @param source - The source object to merge from
 * @returns A new object with merged properties
 */
export function deepMerge<T extends Record<string, any>, U extends Record<string, any>>(
  target: T,
  source: U
): T & U {
  // Handle null/undefined cases
  if (!target || typeof target !== 'object') return source as T & U;
  if (!source || typeof source !== 'object') return target as T & U;

  const result = { ...target } as any;

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = result[key];

      if (isObject(sourceValue) && isObject(targetValue) && !Array.isArray(sourceValue)) {
        // Recursively merge nested objects
        result[key] = deepMerge(targetValue, sourceValue);
      } else {
        // Replace primitive values, arrays, or null/undefined
        result[key] = sourceValue;
      }
    }
  }

  return result;
}

/**
 * Helper function to check if a value is a plain object
 */
function isObject(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
